/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './MobileConfirmationDialog.scss';

import { DefaultButton, IButtonProps, PrimaryButton } from '@fluentui/react/lib/Button';
import { Dialog, DialogFooter, DialogType, IDialogProps } from '@fluentui/react/lib/Dialog';
import * as React from 'react';

export interface ConfirmationDialogProps {
    isOpen: boolean;
    title: string;
    subText?: string;
    // Custom content
    content?: JSX.Element;
    // Method used to dismiss the dialog
    dismiss?: () => void;
    // Possible actions for selection
    actions: IButtonProps[];
    showDarkOverlay?: boolean;
}

export const MobileConfirmationDialogComponent: React.FC<ConfirmationDialogProps> = (
    props: ConfirmationDialogProps
): JSX.Element => {
    const { title, subText, isOpen, content, dismiss, actions, showDarkOverlay } = props || {};
    const buttonClassName = 'mobile-confirmation-dialog';

    const buttons =
        actions &&
        actions.map((buttonProps: IButtonProps, index: number): JSX.Element => {
            return buttonProps.primary ? (
                <span className={buttonClassName}>
                    <PrimaryButton {...buttonProps} key={index} />
                </span>
            ) : (
                <span className={buttonClassName}>
                    <DefaultButton {...buttonProps} key={index} />
                </span>
            );
        });

    const dialogProps: IDialogProps = {
        hidden: !isOpen,
        onDismiss: dismiss,
        styles: {
            main: {
                maxWidth: '500px !important',
                width: '500px !important',
            },
        },
        dialogContentProps: {
            type: DialogType.normal,
            title,
            subText,
        },
        modalProps: {
            isDarkOverlay: !!showDarkOverlay,
            overlay: !showDarkOverlay && {
                isDarkThemed: false,
                styles: {
                    root: {
                        background: 'none',
                    },
                },
            },
        },
    };

    return (
        <Dialog {...dialogProps}>
            {content}
            <DialogFooter>{buttons}</DialogFooter>
        </Dialog>
    );
};

export const ConfirmationDialog = React.memo(MobileConfirmationDialogComponent);
